import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { X, Cpu, AlertCircle, Spline, Component } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api';
import { PopupField, PopupGridField, PopupCoordinates } from './PopupField';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from '@/components/link/Link';
import { API_SOURCE_PATH } from './constants';
import ViewOnGoogleMaps from './ViewOnGoogleMaps';

export interface DevicePopupProps {
	longitude: number;
	latitude: number;
	properties: Record<string, string>;
	onClose: () => void;
}

interface DeviceData {
	id: number;
	site: string;
	siteId: number;
	landowner: string;
	processObjectIds: number[];
	measurementGroupIds: number[];
	latitude: number;
	longitude: number;
}

const fetchDeviceData = async (deviceId: string): Promise<DeviceData> => {
	const response = await apiClient.get(`${API_SOURCE_PATH}/devices/${deviceId}`);
	return response.data;
};

export function DevicePopup({ longitude, latitude, properties, onClose }: DevicePopupProps) {
	const {
		data: fetchedData,
		isLoading,
		error
	} = useQuery({
		queryKey: ['geomap', 'device', properties.id],
		queryFn: () => fetchDeviceData(properties.id),
		enabled: !!properties.id
	});

	const deviceData: DeviceData = {
		id: Number(properties.id) || 0,
		processObjectIds: [],
		measurementGroupIds: [],
		site: properties.site || '',
		siteId: Number(properties.siteId) || 0,
		landowner: properties.landowner || '',
		latitude: latitude,
		longitude: longitude,
		...(fetchedData || {})
	};

	const displayName = properties.name || `Device ${deviceData.id}`;

	return (
		<Card className="bg-white dark:bg-slate-900 border shadow-lg min-w-[260px] max-w-[350px]">
			<CardHeader className="pb-2">
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2 text-sm">
						<Cpu className="h-4 w-4" />
						Device Details
					</CardTitle>
					<Button aria-label="close" variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
						<X className="h-3 w-3" />
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<PopupCoordinates longitude={longitude} latitude={latitude} />
				<div className="grid grid-cols-2 gap-2 text-xs">
					<PopupGridField label="ID" value={deviceData.id.toString()} />
					<PopupGridField label="Site ID" value={deviceData.siteId.toString()} />
				</div>
				<PopupField label="Name" value={displayName} />
				<PopupField label="Site" value={deviceData.site} />
				<PopupField label="Landowner" value={deviceData.landowner} />
				<ViewOnGoogleMaps latitude={latitude} longitude={longitude} />
				{isLoading && (
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-3/4" />
						<Skeleton className="h-4 w-1/2" />
					</div>
				)}
				{error && (
					<div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs">
						<AlertCircle className="h-3 w-3" />
						Failed to load additional data
					</div>
				)}
				{fetchedData && (
					<div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
						<div>
							<span className="text-slate-600 dark:text-slate-400 text-xs">Measurement Groups:</span>
							{deviceData.measurementGroupIds && deviceData.measurementGroupIds.length > 0 ? (
								<div className="flex flex-wrap gap-1 mt-1">
									{deviceData.measurementGroupIds.map(measurementGroupId => (
										<Link
											key={measurementGroupId}
											href={`/measurement-groups/${measurementGroupId}`}
										>
											<Badge
												variant="secondary"
												className="cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1"
											>
												<Component className="size-3" />
												<span>{measurementGroupId}</span>
											</Badge>
										</Link>
									))}
								</div>
							) : (
								<div className="text-slate-500 dark:text-slate-400 text-xs mt-1">N/A</div>
							)}
						</div>

						<div>
							<span className="text-slate-600 dark:text-slate-400 text-xs">Process Objects:</span>
							{deviceData.processObjectIds && deviceData.processObjectIds.length > 0 ? (
								<div className="flex flex-wrap gap-1 mt-1">
									{deviceData.processObjectIds.map(processObjectId => (
										<Link key={processObjectId} href={`/process-objects/${processObjectId}`}>
											<Badge
												variant="secondary"
												className="cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1"
											>
												<Spline className="size-3" />
												<span>{processObjectId}</span>
											</Badge>
										</Link>
									))}
								</div>
							) : (
								<div className="text-slate-500 dark:text-slate-400 text-xs mt-1">N/A</div>
							)}
						</div>
					</div>
				)}{' '}
			</CardContent>
		</Card>
	);
}
